import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { SITE } from "@/data/site";
import { getContactFaqs } from "@/data/faqs";
import { useLang } from "@/lib/i18n";
import GHLForm from "@/components/site/GHLForm";
import FAQ from "@/components/site/FAQ";

export default function Contact() {
  const { t, lang } = useLang();
  const FAQS = getContactFaqs(lang);

  return (
    <>
      <section className="bg-brand-navy-dark text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={SITE.teamImage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ filter: "blur(3px) brightness(0.72) saturate(1.1)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark/78 via-brand-navy-dark/62 to-slate-900/78" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,191,0,0.12)_0%,transparent_55%)]" />
        </div>
        <div className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-brand-navy-light/25 blur-3xl pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
        <div className="relative container-max py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
            <Phone size={14} />
            {t.contact.heroEyebrow}
          </div>
          <h1
            className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl text-balance"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.5)" }}
          >
            {t.contact.heroTitle}
          </h1>
          <p
            className="mt-5 text-white/95 text-lg max-w-2xl leading-relaxed"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.45)" }}
          >
            {t.contact.heroSubPre}
            <a href={`tel:${SITE.primaryPhone}`} className="text-brand-yellow font-semibold underline decoration-brand-yellow underline-offset-4 hover:text-white">
              {t.contact.heroSubLink}
            </a>
            {t.contact.heroSubPost}
          </p>
        </div>
      </section>

      <section className="section-padding bg-[radial-gradient(ellipse_at_top,#EFF6FF_0%,#F8FAFC_50%,#F1F5F9_100%)]">
        <div className="container-max grid lg:grid-cols-[1fr_1.3fr] gap-12">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">{t.common.reachUs}</h2>
            <ul className="mt-6 space-y-3">
              {[
                {
                  icon: Phone,
                  label: t.common.callUs,
                  rows: [
                    { v: SITE.primaryPhone, href: `tel:${SITE.primaryPhone}`, primary: true },
                    { v: SITE.secondaryPhone, href: `tel:${SITE.secondaryPhone}` },
                    { v: SITE.tertiaryPhone, href: `tel:${SITE.tertiaryPhone}` },
                  ],
                },
                { icon: Mail, label: t.common.email, rows: [{ v: SITE.email, href: `mailto:${SITE.email}` }] },
                {
                  icon: MapPin,
                  label: t.common.headquarters,
                  rows: [
                    { v: SITE.address },
                    {
                      v: lang === "es"
                        ? "Sirviendo Palm Beach, Broward, Miami-Dade y Martin"
                        : "Serving Palm Beach, Broward, Miami-Dade & Martin counties",
                      muted: true,
                    },
                  ],
                },
                { icon: Clock, label: t.common.hours, rows: [{ v: SITE.hours }] },
              ].map(({ icon: Icon, label, rows }) => (
                <li key={label} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-brand-yellow/30 text-brand-yellow-dark flex items-center justify-center shrink-0">
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
                    {rows.map((r, i) =>
                      r.href ? (
                        <a
                          key={i}
                          href={r.href}
                          className={`block break-all hover:text-brand-yellow-dark ${
                            r.primary ? "text-xl font-heading font-bold text-slate-900" : "text-slate-700 font-medium"
                          }`}
                        >
                          {r.v}
                        </a>
                      ) : (
                        <p key={i} className={r.muted ? "text-slate-500 text-sm mt-1" : "text-slate-900 font-medium mt-0.5"}>
                          {r.v}
                        </p>
                      )
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 p-5 rounded-2xl bg-brand-navy text-white relative overflow-hidden">
              <span className="absolute top-0 left-0 right-0 h-1 bg-brand-yellow" />
              <p className="text-xs uppercase tracking-wider text-brand-yellow font-semibold">
                {t.common.licensedInsured}
              </p>
              <p className="mt-1 font-heading text-xl font-bold">
                {lang === "es" ? "Plomero Maestro de Florida" : "FL Master Plumber"} #{SITE.license}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-200">
                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-yellow" /> {t.trust[2]}</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-yellow" /> 12 {lang === "es" ? "años en el Sur de Florida" : "years in South Florida"}</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-yellow" /> {lang === "es" ? "Residencial · Comercial · Nueva construcción" : "Residential · Commercial · New construction"}</li>
              </ul>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-3 -right-3 z-10 bg-brand-yellow text-slate-900 rounded-full px-4 py-1.5 text-xs font-bold shadow-md uppercase tracking-wider rotate-3">
              {t.common.freeEstimateTag}
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
              <GHLForm minHeight={840} />
            </div>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} eyebrow={lang === "es" ? "Contacto · FAQ" : "Contact · FAQ"} />
    </>
  );
}
